import { useState, useEffect, memo } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const testimonials = [
  {
    quote:
      "BeginPack made my study abroad dream a reality. From choosing the right university to handling my visa, their guidance was invaluable.",
    name: "Aisha",
    institution: "Study Abroad",
    rating: 5,
  },
  {
    quote:
      "I never imagined the process could be so smooth. BeginPack helped me secure a scholarship and guided me every step of the way.",
    name: "Innocent",
    institution: "Study Abroad",
    rating: 5,
  },
  {
    quote:
      "The team at BeginPack was professional and supportive. They helped me select the perfect course and university for my career goals.",
    name: "Maryam",
    institution: "Study Abroad",
    rating: 5,
  },
  {
    quote:
      "Thanks to BeginPack, I got admission to my dream university in Canada. Their expertise in documentation and visa processing is unmatched.",
    name: "Ayodeji",
    institution: "Study Abroad",
    rating: 4,
  },
  {
    quote:
      "BeginPack's guidance on applications, scholarships, and pre-departure preparation made my transition abroad seamless and stress-free.",
    name: "Kazeem Agibade",
    institution: "Study Abroad",
    rating: 5,
  },
  {
    quote:
      "I'm so grateful for BeginPack! They ensured my visa process went smoothly and even advised me on accommodation and travel.",
    name: "Loveth",
    institution: "Study Abroad",
    rating: 5,
  },
  {
    quote:
      "Their professional approach and personal support made studying abroad an exciting and manageable journey.",
    name: "Stanley Nwosu",
    institution: "Study Abroad",
    rating: 4,
  },
  {
    quote:
      "Booking my flight through BeginPack was fast and easy. I got the best fare and stress-free travel arrangements.",
    name: "Kimberly Peterside",
    institution: "Flight Booking",
    rating: 3,
  },
  {
    quote:
      "BeginPack helped me book my flights and even guided me on airline policies. Excellent service from start to finish.",
    name: "Ike",
    institution: "Flight Booking",
    rating: 5,
  },
  {
    quote:
      "I had a last-minute flight to catch, and BeginPack made it happen quickly. Highly reliable and professional.",
    name: "Tunde",
    institution: "Flight Booking",
    rating: 4,
  },
  {
    quote:
      "They handled my flight booking and gave helpful travel tips. Everything went smoothly thanks to BeginPack.",
    name: "Ruth",
    institution: "Flight Booking",
    rating: 4,
  },
  {
    quote:
      "Affordable, fast, and professional. BeginPack ensured I had the best flight options for my study abroad journey.",
    name: "Ifeanyi",
    institution: "Flight Booking",
    rating: 5,
  },
];

const TestimonialCard = memo(function TestimonialCard({
  testimonial,
}: {
  testimonial: (typeof testimonials)[0];
}) {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200 h-full flex flex-col">
      {/* Stars */}
      <div className="flex gap-1 mb-6">
        {[...Array(testimonial.rating)].map((_, i) => (
          <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
        ))}
      </div>

      {/* Quote */}
      <p className="font-jakarta text-gray-800 text-base leading-relaxed mb-3 flex-grow tracking-tight antialiased">
        {testimonial.quote}
      </p>

      {/* Author */}
      <div className="flex items-center gap-4">
        <div>
          <h4 className="font-poppins font-bold text-gray-900 tracking-tight antialiased">
            {testimonial.name}
          </h4>
          <p className="font-jakarta text-sm text-gray-500 italic tracking-tight antialiased">
            {testimonial.institution}
          </p>
        </div>
      </div>
    </div>
  );
});

function TestimonialsSectionComponent() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const cardsPerView = 3;
  const maxIndex = Math.max(0, testimonials.length - cardsPerView);

  // Auto-scroll every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        if (prev >= maxIndex) {
          return 0; // Loop back to start
        }
        return prev + 1;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [maxIndex]);

  const nextSlide = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const visibleTestimonials = testimonials.slice(
    currentIndex,
    currentIndex + cardsPerView,
  );

  return (
    <section
      className="py-20 bg-gray-50"
      content-visibility="auto"
      contain-intrinsic-size="600px"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-4">
          <span className="font-poppins text-[#800020] font-semibold tracking-tight uppercase text-sm mb-2 block antialiased">
            TESTIMONIALS
          </span>
          <h2 className="font-poppins text-4xl md:text-5xl font-bold text-gray-900 tracking-tight leading-tight antialiased mb-6">
            Stories From Our Clients
          </h2>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 min-h-[400px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 col-span-full"
              >
                {visibleTestimonials.map((testimonial, index) => (
                  <TestimonialCard
                    key={testimonial.name + index}
                    testimonial={testimonial}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center items-center gap-3">
            <button
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className="p-3 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed min-w-[48px] min-h-[48px]"
              aria-label="Previous testimonials"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600" />
            </button>

            <div className="flex gap-2">
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex
                      ? "bg-[#800020] w-8"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              disabled={currentIndex === maxIndex}
              className="p-3 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed min-w-[48px] min-h-[48px]"
              aria-label="Next testimonials"
            >
              <ChevronRight className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default memo(TestimonialsSectionComponent);
